import { useRef, useState } from 'react';
import { supabase } from '../lib/supabase';
import { isNotificationEnabled, enableNotifications, disableNotifications } from '../lib/notifications';
import { t, getLang, setLang } from '../lib/i18n';
import { hashPin } from '../components/LockScreen';
import './SettingsPage.css';

export default function SettingsPage({ user, onNavigate }) {
  const displayName = user.user_metadata?.full_name
    || user.user_metadata?.name
    || user.email?.split('@')[0]
    || '사용자';
  const email = user.email ?? '';
  const avatarLetter = displayName.charAt(0).toUpperCase();

  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('lifelog_dark') === 'true');
  const [notifEnabled, setNotifEnabled] = useState(isNotificationEnabled);
  const [lang, setLangState] = useState(getLang);

  const toggleLang = () => {
    const next = lang === 'ko' ? 'en' : 'ko';
    setLang(next);
    setLangState(next);
  };
  const [showIosGuide, setShowIosGuide] = useState(false);
  const [lockEnabled, setLockEnabled] = useState(() => localStorage.getItem('lifelog_lock_enabled') === 'true');
  const [bioEnabled, setBioEnabled] = useState(() => localStorage.getItem('lifelog_lock_bio') === 'true');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [backupLoading, setBackupLoading] = useState(false);
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [transferLoading, setTransferLoading] = useState(false);
  const [pwLoading, setPwLoading] = useState(false);
  const restoreInputRef = useRef(null);
  const transferInputRef = useRef(null);

  const toggleDarkMode = () => {
    const next = !darkMode;
    setDarkMode(next);
    localStorage.setItem('lifelog_dark', String(next));
    document.body.classList.toggle('dark-mode', next);
  };

  const isStandalone = window.matchMedia('(display-mode: standalone)').matches
    || window.navigator.standalone === true;
  const isIos = /iphone|ipad|ipod/i.test(navigator.userAgent);

  const handleAddToHome = async () => {
    if (isStandalone) {
      alert(t('install_already'));
      return;
    }
    // Android Chrome: use captured prompt
    if (window.__pwaInstallPrompt) {
      const prompt = window.__pwaInstallPrompt;
      prompt.prompt();
      const result = await prompt.userChoice;
      if (result.outcome === 'accepted') {
        window.__pwaInstallPrompt = null;
      }
      return;
    }
    // iOS Safari: show guide
    if (isIos) {
      setShowIosGuide(true);
      return;
    }
    // Other browsers: show generic guide
    alert(t('install_browser_hint'));
  };

  // 이메일 가입 사용자인지 (카카오 등 OAuth가 아닌 경우)
  const isEmailUser = user.app_metadata?.provider === 'email';

  const handleSignOut = async () => {
    if (!window.confirm(t('confirm_logout'))) return;
    await supabase.auth.signOut();
  };

  const handleDeleteAccount = async () => {
    if (!window.confirm('정말로 회원을 탈퇴하시겠습니까?\n모든 기록, 프로필, 카테고리가 영구 삭제되며 복구할 수 없습니다.')) return;
    if (!window.confirm('마지막 확인입니다.\n탈퇴하면 모든 데이터가 삭제됩니다. 계속하시겠습니까?')) return;

    setDeleteLoading(true);
    try {
      const uid = user.id;

      // 1) record_shares 삭제 (records의 FK 참조)
      const { data: myRecords } = await supabase.from('records').select('id').eq('user_id', uid);
      const recordIds = (myRecords || []).map(r => r.id);
      if (recordIds.length > 0) {
        await supabase.from('record_shares').delete().in('record_id', recordIds);
      }

      // 2) records 삭제
      await supabase.from('records').delete().eq('user_id', uid);

      // 3) categories 삭제
      await supabase.from('categories').delete().eq('user_id', uid);

      // 4) profiles 삭제
      await supabase.from('profiles').delete().eq('user_id', uid);

      // 5) localStorage 정리
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('lifelog_')) localStorage.removeItem(key);
      });

      // 6) 로그아웃
      await supabase.auth.signOut();
      alert('회원 탈퇴가 완료되었습니다.');
    } catch (e) {
      alert('탈퇴 처리 중 오류가 발생했습니다: ' + e.message);
    }
    setDeleteLoading(false);
  };

  const handleToggleLock = () => {
    if (lockEnabled) {
      // 잠금 해제: PIN 확인
      const input = window.prompt('현재 비밀번호 4자리를 입력하세요');
      if (!input) return;
      if (hashPin(input) !== localStorage.getItem('lifelog_lock_pin')) {
        alert('비밀번호가 틀렸습니다.');
        return;
      }
      localStorage.removeItem('lifelog_lock_enabled');
      localStorage.removeItem('lifelog_lock_pin');
      localStorage.removeItem('lifelog_lock_bio');
      localStorage.removeItem('lifelog_lock_bio_cred');
      setLockEnabled(false);
      setBioEnabled(false);
    } else {
      // 잠금 설정: PIN 입력
      const pin = window.prompt('설정할 비밀번호 4자리를 입력하세요');
      if (!pin || pin.length !== 4 || !/^\d{4}$/.test(pin)) {
        alert('숫자 4자리를 입력해주세요.');
        return;
      }
      const confirm = window.prompt('비밀번호를 다시 입력하세요');
      if (pin !== confirm) {
        alert('비밀번호가 일치하지 않습니다.');
        return;
      }
      localStorage.setItem('lifelog_lock_pin', hashPin(pin));
      localStorage.setItem('lifelog_lock_enabled', 'true');
      setLockEnabled(true);
    }
  };

  const handleChangeLockPin = () => {
    const current = window.prompt('현재 비밀번호 4자리를 입력하세요');
    if (!current) return;
    if (hashPin(current) !== localStorage.getItem('lifelog_lock_pin')) {
      alert('비밀번호가 틀렸습니다.');
      return;
    }
    const newPin = window.prompt('새 비밀번호 4자리를 입력하세요');
    if (!newPin || newPin.length !== 4 || !/^\d{4}$/.test(newPin)) {
      alert('숫자 4자리를 입력해주세요.');
      return;
    }
    const confirm = window.prompt('새 비밀번호를 다시 입력하세요');
    if (newPin !== confirm) {
      alert('비밀번호가 일치하지 않습니다.');
      return;
    }
    localStorage.setItem('lifelog_lock_pin', hashPin(newPin));
    alert('비밀번호가 변경되었습니다.');
  };

  const handleToggleBio = async () => {
    if (bioEnabled) {
      localStorage.removeItem('lifelog_lock_bio');
      localStorage.removeItem('lifelog_lock_bio_cred');
      setBioEnabled(false);
      return;
    }

    if (!window.PublicKeyCredential) {
      alert('이 브라우저에서는 생체인증을 지원하지 않습니다.');
      return;
    }

    try {
      const available = await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
      if (!available) {
        alert('이 기기에서는 생체인증을 사용할 수 없습니다.\n(지문 또는 얼굴 인식이 설정되어 있는지 확인해주세요)');
        return;
      }

      const challenge = crypto.getRandomValues(new Uint8Array(32));
      const credential = await navigator.credentials.create({
        publicKey: {
          challenge,
          rp: { name: '인생로그', id: window.location.hostname },
          user: {
            id: new TextEncoder().encode(user.id),
            name: user.email || 'user',
            displayName: displayName,
          },
          pubKeyCredParams: [{ alg: -7, type: 'public-key' }],
          authenticatorSelection: {
            authenticatorAttachment: 'platform',
            userVerification: 'required',
          },
          timeout: 60000,
        },
      });

      if (credential) {
        const credId = btoa(String.fromCharCode(...new Uint8Array(credential.rawId)));
        localStorage.setItem('lifelog_lock_bio_cred', credId);
        localStorage.setItem('lifelog_lock_bio', 'true');
        setBioEnabled(true);
      }
    } catch (e) {
      if (e.name !== 'NotAllowedError') {
        alert('생체인증 등록 실패: ' + e.message);
      }
    }
  };

  const handleChangePassword = async () => {
    const currentPw = window.prompt('현재 비밀번호를 입력하세요');
    if (!currentPw) return;

    setPwLoading(true);
    // 현재 비밀번호 확인
    const { error: verifyErr } = await supabase.auth.signInWithPassword({
      email,
      password: currentPw,
    });
    if (verifyErr) {
      setPwLoading(false);
      alert('현재 비밀번호가 일치하지 않습니다.');
      return;
    }

    const newPw = window.prompt('새 비밀번호를 입력하세요 (6자 이상)');
    if (!newPw) { setPwLoading(false); return; }
    if (newPw.length < 6) {
      setPwLoading(false);
      alert('비밀번호는 6자 이상이어야 합니다.');
      return;
    }

    const { error } = await supabase.auth.updateUser({ password: newPw });
    setPwLoading(false);
    if (error) {
      alert('변경 실패: ' + error.message);
    } else {
      alert('비밀번호가 변경되었습니다.');
    }
  };

  const handleBackup = async () => {
    setBackupLoading(true);
    try {
      const [
        { data: profilesData },
        { data: categoriesData },
        { data: recordsData },
      ] = await Promise.all([
        supabase.from('profiles').select('*').eq('user_id', user.id),
        supabase.from('categories').select('*').eq('user_id', user.id),
        supabase.from('records').select('*').eq('user_id', user.id),
      ]);

      const recordIds = (recordsData || []).map((r) => r.id);
      let sharesData = [];
      if (recordIds.length > 0) {
        const { data } = await supabase
          .from('record_shares')
          .select('*')
          .in('record_id', recordIds);
        sharesData = data || [];
      }

      const backup = {
        version: 1,
        backup_date: new Date().toISOString(),
        data: {
          profiles: profilesData || [],
          categories: categoriesData || [],
          records: recordsData || [],
          record_shares: sharesData,
        },
      };

      const blob = new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `lifelog-backup-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      alert('백업 실패: ' + e.message);
    }
    setBackupLoading(false);
  };

  const handleRestoreFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    if (!window.confirm('백업 파일의 데이터를 복원하시겠습니까?\n기존 데이터와 병합됩니다.')) return;

    setRestoreLoading(true);
    try {
      const text = await file.text();
      const backup = JSON.parse(text);

      if (!backup?.data) {
        alert('올바른 백업 파일이 아닙니다.');
        setRestoreLoading(false);
        return;
      }

      const { profiles, categories, records, record_shares } = backup.data;
      const currentUserId = user.id;
      const errors = [];
      const newId = () => crypto.randomUUID();

      // ===== 1) 프로필: "-복구" 접미사 붙여서 새로 생성 =====
      const profileMap = new Map(); // old profile id → new profile id
      if (profiles?.length) {
        const newProfiles = profiles.map(p => {
          const nid = newId();
          profileMap.set(p.id, nid);
          return {
            ...p,
            id: nid,
            user_id: currentUserId,
            name: p.name + '-복구',
          };
        });
        const { error } = await supabase.from('profiles').insert(newProfiles);
        if (error) errors.push('프로필: ' + error.message);
      }

      // ===== 2) 카테고리: 이름 중복 건너뛰고 새 것만 추가 =====
      if (categories?.length) {
        const { data: existingCats } = await supabase
          .from('categories')
          .select('name')
          .eq('user_id', currentUserId);
        const existingNames = new Set((existingCats || []).map(c => c.name));
        const newCats = categories
          .filter(c => !existingNames.has(c.name))
          .map(c => ({ ...c, id: newId(), user_id: currentUserId }));
        if (newCats.length) {
          const { error } = await supabase.from('categories').insert(newCats);
          if (error) errors.push('카테고리: ' + error.message);
        }
      }

      // ===== 3) 기록: 새 ID, profile_id를 복구된 프로필로 매핑 =====
      const recordMap = new Map(); // old record id → new record id
      if (records?.length) {
        const newRecords = records.map(r => {
          const nid = newId();
          recordMap.set(r.id, nid);
          return {
            ...r,
            id: nid,
            user_id: currentUserId,
            profile_id: profileMap.get(r.profile_id) || null,
          };
        });
        const { error } = await supabase.from('records').insert(newRecords);
        if (error) errors.push('기록: ' + error.message);
      }

      // ===== 4) record_shares: 복구 프로필 ↔ 복구 기록 연결 =====
      if (record_shares?.length) {
        const newShares = record_shares
          .filter(s => recordMap.has(s.record_id) && profileMap.has(s.profile_id))
          .map(s => ({
            id: newId(),
            record_id: recordMap.get(s.record_id),
            profile_id: profileMap.get(s.profile_id),
          }));
        if (newShares.length) {
          const { error } = await supabase.from('record_shares').insert(newShares);
          if (error) errors.push('공유설정: ' + error.message);
        }
      }

      // record_shares가 없는 복구 기록 → 첫 번째 복구 프로필에 연결
      const firstNewProfileId = profileMap.size > 0 ? [...profileMap.values()][0] : null;
      if (firstNewProfileId && recordMap.size > 0) {
        const newRecordIds = [...recordMap.values()];
        const { data: existingShares } = await supabase
          .from('record_shares')
          .select('record_id')
          .in('record_id', newRecordIds);
        const sharedSet = new Set(existingShares?.map(s => s.record_id) || []);
        const missing = newRecordIds.filter(id => !sharedSet.has(id));
        if (missing.length > 0) {
          await supabase.from('record_shares').insert(
            missing.map(id => ({ record_id: id, profile_id: firstNewProfileId }))
          );
        }
      }

      // ===== 5) 카테고리 중복 제거 (이름 기준, 가장 오래된 것만 유지) =====
      const { data: allCats } = await supabase
        .from('categories')
        .select('id, name')
        .eq('user_id', currentUserId)
        .order('created_at', { ascending: true });
      if (allCats) {
        const seen = new Set();
        const dupeIds = [];
        for (const cat of allCats) {
          if (seen.has(cat.name)) {
            dupeIds.push(cat.id);
          } else {
            seen.add(cat.name);
          }
        }
        if (dupeIds.length > 0) {
          await supabase.from('categories').delete().in('id', dupeIds);
        }
      }

      const profNames = profiles?.map(p => p.name + '-복구').join(', ') || '';
      if (errors.length > 0) {
        alert(`복원 중 일부 오류:\n${errors.join('\n')}\n\n나머지는 정상 복원되었습니다.`);
      } else {
        alert(`복원 완료!\n프로필: ${profNames}\n카테고리 ${categories?.length ?? 0}개, 기록 ${records?.length ?? 0}개`);
      }

      localStorage.removeItem(`lifelog_share_migration_v1_${currentUserId}`);
      window.location.reload();
    } catch (e) {
      alert('복원 실패: ' + e.message);
    }
    setRestoreLoading(false);
  };

  const handleTransferFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    setTransferLoading(true);
    try {
      const text = await file.text();
      const transfer = JSON.parse(text);

      if (transfer?.type !== 'profile_transfer' || !transfer?.records) {
        alert('올바른 승계 파일이 아닙니다.');
        setTransferLoading(false);
        return;
      }

      const { sender_name, profile, stats, records, categories } = transfer;
      const daysText = stats.total_days?.toLocaleString() || '0';

      if (!window.confirm(
        `${sender_name}님이 "${profile.name}" 프로필의\n` +
        `${daysText}일간 기록한 인생을 보내드립니다.\n\n` +
        `기록 ${stats.record_count}개 | ${stats.first_date} ~ ${stats.last_date}\n\n` +
        `이 프로필을 받으시겠습니까?`
      )) {
        setTransferLoading(false);
        return;
      }

      const currentUserId = user.id;
      const newId = () => crypto.randomUUID();
      const errors = [];

      // 1) 프로필 생성
      const newProfileId = newId();
      const { error: profErr } = await supabase.from('profiles').insert({
        id: newProfileId,
        user_id: currentUserId,
        name: profile.name,
        emoji: profile.emoji,
      });
      if (profErr) errors.push('프로필: ' + profErr.message);

      // 2) 기록 생성
      const recordMap = new Map();
      if (records.length > 0) {
        const newRecords = records.map((r) => {
          const nid = newId();
          recordMap.set(r.id, nid);
          return {
            ...r,
            id: nid,
            user_id: currentUserId,
            profile_id: newProfileId,
          };
        });
        const { error } = await supabase.from('records').insert(newRecords);
        if (error) errors.push('기록: ' + error.message);
      }

      // 3) record_shares 생성
      if (recordMap.size > 0) {
        const newShares = [...recordMap.values()].map((rid) => ({
          id: newId(),
          record_id: rid,
          profile_id: newProfileId,
        }));
        const { error } = await supabase.from('record_shares').insert(newShares);
        if (error) errors.push('공유설정: ' + error.message);
      }

      // 4) 카테고리 추가 (중복 건너뛰기)
      if (categories?.length > 0) {
        const { data: existingCats } = await supabase
          .from('categories')
          .select('name')
          .eq('user_id', currentUserId);
        const existingNames = new Set((existingCats || []).map((c) => c.name));
        const newCats = categories
          .filter((c) => !existingNames.has(c.name))
          .map((c) => ({ id: newId(), user_id: currentUserId, name: c.name, icon: c.icon }));
        if (newCats.length > 0) {
          const { error } = await supabase.from('categories').insert(newCats);
          if (error) errors.push('카테고리: ' + error.message);
        }
      }

      if (errors.length > 0) {
        alert(`승계 중 일부 오류:\n${errors.join('\n')}\n\n나머지는 정상 처리되었습니다.`);
      } else {
        alert(
          `승계 완료!\n\n` +
          `${profile.emoji} ${profile.name} 프로필과\n` +
          `${stats.record_count}개의 기록을 받았습니다.`
        );
      }

      window.location.reload();
    } catch (e) {
      alert('승계 파일 처리 실패: ' + e.message);
    }
    setTransferLoading(false);
  };

  return (
    <div className="settings-page">
      <header className="settings-header">
        <span className="settings-title">{t('settings_title')}</span>
      </header>

      <div className="settings-body">

        {/* 프로필 카드 */}
        <div className="profile-card">
          <div className="profile-avatar">{avatarLetter}</div>
          <div className="profile-info">
            <p className="profile-name">{displayName}</p>
            {email && <p className="profile-email">{email}</p>}
          </div>
        </div>

        {/* 백업 & 복원 */}
        <div className="settings-group">
          <p className="settings-group-label">{t('settings_backup')}</p>
          <div className="settings-list">
            <button
              className="settings-row"
              onClick={handleBackup}
              disabled={backupLoading}
            >
              <span className="settings-row-icon">📤</span>
              <span className="settings-row-label">
                {backupLoading ? t('settings_backup_loading') : t('settings_backup_btn')}
              </span>
            </button>
            <button
              className="settings-row"
              onClick={() => restoreInputRef.current?.click()}
              disabled={restoreLoading}
            >
              <span className="settings-row-icon">📥</span>
              <span className="settings-row-label">
                {restoreLoading ? t('settings_restore_loading') : t('settings_restore_btn')}
              </span>
            </button>
            <button
              className="settings-row"
              onClick={() => onNavigate('export')}
            >
              <span className="settings-row-icon">🖨️</span>
              <span className="settings-row-label">PDF인쇄 / 앨범만들기</span>
              <svg className="settings-row-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 18l6-6-6-6" />
              </svg>
            </button>
          </div>
          <p className="settings-group-hint">
            {t('settings_backup_hint')}
            <br />
            소중한 자료를 위해 꾸준한 백업을 권장드립니다. 데이터 손실 및 삭제에 대해 책임지지 않습니다.
          </p>
        </div>

        {/* 프로필 승계 */}
        <div className="settings-group">
          <p className="settings-group-label">{t('settings_transfer')}</p>
          <div className="settings-list">
            <button
              className="settings-row"
              onClick={() => transferInputRef.current?.click()}
              disabled={transferLoading}
            >
              <span className="settings-row-icon">📨</span>
              <span className="settings-row-label">
                {transferLoading ? t('settings_transfer_loading') : t('settings_transfer_btn')}
              </span>
            </button>
          </div>
          <p className="settings-group-hint">
            {t('settings_transfer_hint')}
            <br />
            {t('settings_transfer_hint2')}
          </p>
        </div>

        {/* 잠금 설정 */}
        <div className="settings-group">
          <p className="settings-group-label">잠금 설정</p>
          <div className="settings-list">
            <label className="settings-row settings-row--static">
              <span className="settings-row-icon">🔐</span>
              <span className="settings-row-label">앱 잠금</span>
              <span className="settings-toggle">
                <input type="checkbox" checked={lockEnabled} onChange={handleToggleLock} />
                <span className="toggle-track"><span className="toggle-thumb" /></span>
              </span>
            </label>
            {lockEnabled && (
              <>
                <button className="settings-row" onClick={handleChangeLockPin}>
                  <span className="settings-row-icon">🔢</span>
                  <span className="settings-row-label">비밀번호 변경</span>
                  <svg className="settings-row-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 18l6-6-6-6" />
                  </svg>
                </button>
                <label className="settings-row settings-row--static">
                  <span className="settings-row-icon">👆</span>
                  <span className="settings-row-label">생체인증 (지문/얼굴)</span>
                  <span className="settings-toggle">
                    <input type="checkbox" checked={bioEnabled} onChange={handleToggleBio} />
                    <span className="toggle-track"><span className="toggle-thumb" /></span>
                  </span>
                </label>
              </>
            )}
          </div>
          <p className="settings-group-hint">
            앱을 열 때 비밀번호 또는 생체인증으로 잠금을 해제합니다.
          </p>
        </div>

        {/* 화면 설정 */}
        <div className="settings-group">
          <p className="settings-group-label">{t('settings_display')}</p>
          <div className="settings-list">
            <label className="settings-row settings-row--static">
              <span className="settings-row-icon">🌙</span>
              <span className="settings-row-label">{t('settings_dark_mode')}</span>
              <span className="settings-toggle">
                <input type="checkbox" checked={darkMode} onChange={toggleDarkMode} />
                <span className="toggle-track"><span className="toggle-thumb" /></span>
              </span>
            </label>
            <label className="settings-row settings-row--static">
              <span className="settings-row-icon">🔔</span>
              <span className="settings-row-label">{t('settings_notification')}</span>
              <span className="settings-toggle">
                <input
                  type="checkbox"
                  checked={notifEnabled}
                  onChange={async () => {
                    if (notifEnabled) {
                      disableNotifications();
                      setNotifEnabled(false);
                    } else {
                      const ok = await enableNotifications();
                      setNotifEnabled(ok);
                    }
                  }}
                />
                <span className="toggle-track"><span className="toggle-thumb" /></span>
              </span>
            </label>
            <label className="settings-row settings-row--static">
              <span className="settings-row-icon">🌐</span>
              <span className="settings-row-label">{t('settings_language')}</span>
              <button className="lang-toggle-btn" onClick={toggleLang}>
                {lang === 'ko' ? '한국어 → English' : 'English → 한국어'}
              </button>
            </label>

            {!isStandalone && (
              <button className="settings-row" onClick={handleAddToHome}>
                <span className="settings-row-icon">📲</span>
                <span className="settings-row-label">{t('settings_add_home')}</span>
                <svg className="settings-row-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </button>
            )}
          </div>
          <p className="settings-group-hint">
            {t('settings_notification_hint')}
          </p>
        </div>

        {/* iOS 홈 화면 추가 안내 모달 */}
        {showIosGuide && (
          <div className="ios-guide-overlay" onClick={() => setShowIosGuide(false)}>
            <div className="ios-guide-modal" onClick={e => e.stopPropagation()}>
              <h3>{t('install_ios_title')}</h3>
              <div className="ios-guide-steps">
                <p>1. {t('install_ios_step1')}</p>
                <p>2. {t('install_ios_step2')}</p>
                <p>3. {t('install_ios_step3')}</p>
              </div>
              <button className="ios-guide-close" onClick={() => setShowIosGuide(false)}>
                {t('install_ios_close')}
              </button>
            </div>
          </div>
        )}

        {/* 통계 */}
        <div className="settings-group">
          <p className="settings-group-label">통계</p>
          <div className="settings-list">
            <button className="settings-row" onClick={() => onNavigate('stats')}>
              <span className="settings-row-icon">📊</span>
              <span className="settings-row-label">통계 / 대시보드</span>
              <svg className="settings-row-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 18l6-6-6-6" />
              </svg>
            </button>
          </div>
        </div>

        {/* 계정 */}
        <div className="settings-group">
          <p className="settings-group-label">{t('settings_account')}</p>
          <div className="settings-list">
            {isEmailUser && (
              <button className="settings-row" onClick={handleChangePassword} disabled={pwLoading}>
                <span className="settings-row-icon">🔒</span>
                <span className="settings-row-label">
                  {pwLoading ? t('settings_changing_pw') : t('settings_change_pw')}
                </span>
                <svg className="settings-row-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </button>
            )}
            <button className="settings-row settings-row--danger" onClick={handleSignOut}>
              <span className="settings-row-icon">🚪</span>
              <span className="settings-row-label">{t('settings_logout')}</span>
            </button>
            <button
              className="settings-row settings-row--danger"
              onClick={handleDeleteAccount}
              disabled={deleteLoading}
            >
              <span className="settings-row-icon">⚠️</span>
              <span className="settings-row-label">
                {deleteLoading ? '탈퇴 처리 중...' : '회원 탈퇴'}
              </span>
            </button>
          </div>
        </div>

        {/* 앱 정보 */}
        <div className="settings-group">
          <p className="settings-group-label">{t('settings_app_info')}</p>
          <div className="settings-list">
            <div className="settings-row settings-row--static">
              <span className="settings-row-icon">📔</span>
              <span className="settings-row-label">인생로그 - 나의 인생을 기록하다</span>
              <span className="settings-row-value">v1.0.0</span>
            </div>
            <a className="settings-row" href="https://open.kakao.com/o/sQnNs8ii" target="_blank" rel="noopener noreferrer">
              <span className="settings-row-icon">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path fillRule="evenodd" clipRule="evenodd" d="M10 2C5.582 2 2 4.79 2 8.222c0 2.178 1.417 4.09 3.567 5.188l-.91 3.39a.25.25 0 0 0 .378.274L9.1 14.678c.296.028.597.044.9.044 4.418 0 8-2.79 8-6.222C18 4.79 14.418 2 10 2z" fill="#3C1E1E"/>
                </svg>
              </span>
              <span className="settings-row-label">문의, 개선, 버그 문의 (카카오톡)</span>
              <svg className="settings-row-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 18l6-6-6-6" />
              </svg>
            </a>
            <button className="settings-row" onClick={() => window.open('/privacy', '_blank')}>
              <span className="settings-row-icon">📄</span>
              <span className="settings-row-label">개인정보처리방침</span>
              <svg className="settings-row-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 18l6-6-6-6" />
              </svg>
            </button>
            <button className="settings-row" onClick={() => window.open('/terms', '_blank')}>
              <span className="settings-row-icon">📋</span>
              <span className="settings-row-label">이용약관</span>
              <svg className="settings-row-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 18l6-6-6-6" />
              </svg>
            </button>
          </div>
        </div>

      </div>

      {/* 복원용 숨김 파일 입력 */}
      <input
        ref={restoreInputRef}
        type="file"
        accept="*/*"
        style={{ display: 'none' }}
        onChange={handleRestoreFile}
      />
      {/* 승계용 숨김 파일 입력 */}
      <input
        ref={transferInputRef}
        type="file"
        accept="*/*"
        style={{ display: 'none' }}
        onChange={handleTransferFile}
      />
    </div>
  );
}
